import { createContext, useCallback, useContext, useEffect, useReducer, useSyncExternalStore } from 'react';
import { useStore } from 'zustand';
import type { Engine, UIState } from '../engine/Engine';

export const EngineContext = createContext<Engine | null>(null);

export function useEngine(): Engine {
  const e = useContext(EngineContext);
  if (!e) throw new Error('useEngine outside of a board');
  return e;
}

export function useUI<T>(selector: (s: UIState) => T): T {
  return useStore(useEngine().ui, selector);
}

/** A value from the board document's shared `meta` map (timer, voting…). */
export function useMeta<T>(key: string): T | undefined {
  const e = useEngine();
  const subscribe = useCallback(
    (cb: () => void) => {
      e.doc.meta.observe(cb);
      return () => e.doc.meta.unobserve(cb);
    },
    [e],
  );
  return useSyncExternalStore(subscribe, () => e.doc.meta.get(key) as T | undefined);
}

/** Re-renders (at most once a frame) whenever board items change: for overlays pinned to items. */
export function useDocTick(): void {
  const e = useEngine();
  const [, bump] = useReducer((n: number) => n + 1, 0);
  useEffect(() => {
    let frame = 0;
    const off = e.doc.onChange(() => {
      if (!frame) frame = requestAnimationFrame(() => ((frame = 0), bump()));
    });
    return () => {
      off();
      cancelAnimationFrame(frame);
    };
  }, [e]);
}

/** Re-renders every `ms` while `ms` is set (countdowns). */
export function useTicker(ms: number | null): void {
  const [, bump] = useReducer((n: number) => n + 1, 0);
  useEffect(() => {
    if (ms === null) return;
    const id = window.setInterval(bump, ms);
    return () => clearInterval(id);
  }, [ms]);
}
